import resourceTypesJson from './resource-types.json'
import {
  GENERATED_DEVOPS_RESOURCE_TYPES,
  GENERATED_PLC_RESOURCE_TYPES,
  GENERATED_SCADA_RESOURCE_TYPES,
  type DevopsResourceType,
  type PlcResourceType,
  type ScadaResourceType
} from './resource-types.generated.js'

export type { DevopsResourceType, PlcResourceType, ScadaResourceType }

export type ScadaResourceTypeDefinition = {
  type: ScadaResourceType
  ontology_compute_type: string
}

export type PlcResourceTypeDefinition = {
  type: PlcResourceType
  ontology_vendor: string
  source_extensions: readonly string[]
}

export type DevopsResourceTypeCatalog = {
  version: number
  scada: readonly ScadaResourceTypeDefinition[]
  plc: readonly PlcResourceTypeDefinition[]
}

const catalog = resourceTypesJson as DevopsResourceTypeCatalog

function assertGeneratedTypesMatch(
  category: string,
  actual: readonly string[],
  generated: readonly string[]
): void {
  if (
    actual.length !== generated.length ||
    actual.some((value, index) => value !== generated[index])
  ) {
    throw new Error(
      `DevOps resource-type ${category} declarations are stale; run the package generator`
    )
  }
}

if (!Number.isInteger(catalog.version) || catalog.version <= 0) {
  throw new Error('DevOps resource-type catalog version must be a positive integer')
}

const scadaTypes = catalog.scada.map((definition) => definition.type)
const plcTypes = catalog.plc.map((definition) => definition.type)
const allTypes = [...scadaTypes, ...plcTypes]

assertGeneratedTypesMatch('SCADA', scadaTypes, GENERATED_SCADA_RESOURCE_TYPES)
assertGeneratedTypesMatch('PLC', plcTypes, GENERATED_PLC_RESOURCE_TYPES)
assertGeneratedTypesMatch('combined', allTypes, GENERATED_DEVOPS_RESOURCE_TYPES)

export const DEVOPS_RESOURCE_TYPE_CATALOG = Object.freeze(catalog)
export const SCADA_RESOURCE_TYPES = Object.freeze(
  scadaTypes
) as unknown as typeof GENERATED_SCADA_RESOURCE_TYPES
export const PLC_RESOURCE_TYPES = Object.freeze(
  plcTypes
) as unknown as typeof GENERATED_PLC_RESOURCE_TYPES
export const DEVOPS_RESOURCE_TYPES = Object.freeze(
  allTypes
) as unknown as typeof GENERATED_DEVOPS_RESOURCE_TYPES

const resourceTypeSet: ReadonlySet<string> = new Set(DEVOPS_RESOURCE_TYPES)

export function isDevopsResourceType(value: string): value is DevopsResourceType {
  return resourceTypeSet.has(value)
}

/**
 * Maps canonical ontology PLC metadata to its configured DevOps resource type.
 * Source-file extensions are consulted only when vendor metadata is absent.
 */
export function classifyPlc(
  vendor: string | null | undefined,
  sourceFile: string | null | undefined
): PlcResourceType | undefined {
  const normalizedVendor = vendor?.trim()
  if (normalizedVendor) {
    return catalog.plc.find(
      (definition) =>
        definition.ontology_vendor.toLowerCase() === normalizedVendor.toLowerCase()
    )?.type
  }

  const normalizedSource = sourceFile?.trim().toLowerCase()
  if (!normalizedSource) return undefined

  return catalog.plc.find((definition) =>
    definition.source_extensions.some((extension) => normalizedSource.endsWith(extension))
  )?.type
}
